import { FunctionalComponent } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { Nav } from '@accedo/spatial-nav';
import { Button as CtvButton } from '@accedo/ctv-button';
import { KeyCodes } from '@accedo/virtual-key';
import { deviceApi, ExitReasonType } from '@accedo/ctv-device-api';
import { stylesheet } from 'astroturf';
import { useErrorScreen } from '##/components/Error';
import { useInterval } from '##/utils/hooks';
import { AuthenticationResponse } from '##/services/tenApi/auth/types';
import { nav } from '##/init';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator/LoadingIndicator';
import ActivationScreenBg from '##/assets/images/ActivationScreenBg.jpg';
import { config, focusCache } from '##/services/store/store';
import { PageNavigation } from '##/utils/PageNavigation';
import tenApi from '##/services/tenApi';
import KeyEventHelper from '##/utils/KeyEventHelper';
import {
  TealiumErrorCode,
  TealiumErrorContext,
} from '##/services/analytics/Tealium/TealiumErrorTypes';
import { isLoggedIn } from '##/services/authentication/Authentication';
import { usePlatformMessaging } from '##/services/platformMessaging/PlatformMessagingContext';
import { fetchFMSResponse } from '##/services/tenApi/fms';
import { setNavVisibility } from '../SideNav/SideNav';
import { StandardCarouselItem } from '../StandardCarousel/types';
import { LiveCarouselItem } from '../LiveCarousel/types';

const ACTIVATION_TEXT = 'Simply scan the QR code or head to:';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .screen-container {
    width: 100vw;
    height: 100vh;
  }

  .background-image {
    height: 100vh;
    width: 100vw;
    z-index: -1;
    position: absolute;
    top: 0;
  }

  .content-container {
    font-family: barlow;
    height: pxToVw(424);
    width: pxToVw(520);
    display: flex;
    flex-direction: column;
    margin-left: pxToVw(129);
    margin-top: pxToVw(139);
  }

  .content-header {
    height: pxToVw(31);
    width: 100vw;
    font-size: pxToVw(26);
    margin-bottom: pxToVw(34);
  }

  .number {
    border-radius: 100%;
    width: pxToVw(25);
    height: pxToVw(25);
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $pure-white;
    color: $pure-black;
    margin-right: pxToVw(17);
  }

  p {
    font-size: pxToVw(16);
    height: pxToVw(19);
    margin: 0;
  }

  .content {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: pxToVw(25);
  }

  .content-one {
    width: 100vw;
    height: pxToVw(65);
    margin-bottom: pxToVw(34);
    .website {
      font-size: pxToVw(26);
      margin: 0;
      margin-top: pxToVw(10);
      margin-left: pxToVw(42);
      color: $light-blue;
    }
  }
  .content-two {
    width: 100vw;
    height: pxToVw(120);
    .entry-code {
      font-size: pxToVw(90);
      margin: 0;
      margin-left: pxToVw(42);
      height: pxToVw(100);
      font-weight: 500;
      color: $light-blue;
    }
  }

  .button-container {
    margin-top: pxToVw(25);
    height: pxToVw(120);
    .button {
      font-family: barlowsemicondensed;
      border: pxToVw(1) solid $button-grey;
      height: pxToVw(40);
      width: pxToVw(244);
      border-radius: pxToVw(31);
      margin-right: pxToVw(12);
      padding-bottom: pxToVh(2);
      font-size: pxToVh(18);
      font-weight: 500;
      -webkit-text-stroke: 0;
      margin-top: pxToVw(60);
      &:global(.current-focus) {
        background-color: $light-blue;
        outline: none;
        color: $pure-white;
      }
    }
  }
`;

const ALT_TOKEN = 'altToken';
const REFRESH_TOKEN = 'refreshToken';
const GUIDED_SIGN_IN_DELAY = 5000;
const SETTINGS_ROUTE = '/setting';

const openShowPage = (showId: string): void => {
  const { showsApiEndpoint: showsBaseUrl } = config.$get().app;
  const showEndpoint = `${showsBaseUrl}/${showId}`;

  PageNavigation.replaceRoute(`/show/${showId}`, showEndpoint);
};

const clearSettingState = (): void =>
  PageNavigation.setCurrentState({ secondaryNavSlug: '' }, SETTINGS_ROUTE);

const ActivationScreen: FunctionalComponent<{}> = () => {
  const [authData, setAuthData] = useState<AuthenticationResponse>();
  const { catchApiError, showError, sendErrorToTealium } = useErrorScreen();
  const { sendMessageToPlatform } = usePlatformMessaging();

  const pageId = 'activation-page';
  const continueButton = 'continue-browsing-button';
  const FAQButton = 'faq-button';
  const isGuidedSignIn = focusCache.activationScreen.$get().signInDeeplinked;

  const onFaqClick = (): void => {
    PageNavigation.replaceRoute(SETTINGS_ROUTE, { secondaryNavSlug: 'faqs' });
  };

  const handleBackNavigation = (): void => {
    if (PageNavigation.getNthLastPath(2) === SETTINGS_ROUTE) {
      PageNavigation.replaceRoute('/');
    } else {
      PageNavigation.handleGlobalBackKey();
    }
    clearSettingState();
  };

  const sendGuidedSetupStatusThenExit = async (
    status: 'success' | 'error' | 'backedout' | 'alreadyloggedin',
  ): Promise<void> => {
    const statusMap = {
      success: 'appSignOnSuccessful',
      error: 'appSignOnError',
      backedout: 'appSignOnBackedOut',
      alreadyloggedin: 'appSignOnSuccessful',
    };
    sendMessageToPlatform({
      event: statusMap[status],
    });
    setTimeout(() => {
      deviceApi.exit(ExitReasonType.ERROR);
    }, GUIDED_SIGN_IN_DELAY);
  };

  const getAuthCode = (requestNo = 1): void => {
    tenApi.auth
      .getActivationCode()
      .then((data) => {
        setAuthData(data);
        clearSettingState();
      })
      .catch((err) => {
        if (requestNo > 3) {
          if (isGuidedSignIn) {
            sendGuidedSetupStatusThenExit('error');
          }
          showError('GENERIC_ERROR');
        } else {
          setTimeout(() => getAuthCode(requestNo + 1), 5000);
        }
        const { generateCode } = config.$get().app.authConfig;
        sendErrorToTealium({
          errorCode: TealiumErrorCode.FetchActivationCode,
          errorContext: TealiumErrorContext.API,
          errorDetail: JSON.stringify(err),
          errorSource: generateCode,
          errorMessage: '',
        });
      });
  };

  useEffect(() => {
    setNavVisibility?.(false);
    setLoadingIndicatorVisibility?.(false);
    if (!isGuidedSignIn) {
      nav.assignFocus(continueButton);
    }
    getAuthCode();
    KeyEventHelper.addKeydownListener(
      (keyName) => {
        if (!isGuidedSignIn && keyName === KeyCodes.vKey.BACK) {
          handleBackNavigation();

          return true;
        }

        return (
          keyName === KeyCodes.vKey.LEFT &&
          nav.currentFocusNodeId === continueButton
        );
      },

      'activation-screen-listener',
    );
    KeyEventHelper.addKeydownListener((keyName) => {
      let stopEventPropagation = false;
      if (isGuidedSignIn && keyName === KeyCodes.vKey.BACK) {
        sendGuidedSetupStatusThenExit('backedout');
        stopEventPropagation = true;
      }
      return stopEventPropagation;
    }, 'guided-sign-in-listener');
    if (isLoggedIn() && isGuidedSignIn) {
      sendGuidedSetupStatusThenExit('alreadyloggedin');
    }
    return () => {
      setNavVisibility?.(true);
      KeyEventHelper.removeKeydownListener('activation-screen-listener');
      KeyEventHelper.removeKeydownListener('guided-sign-in-listener');
    };
  }, []);

  useInterval(async () => {
    if (authData) {
      tenApi.auth
        .getActivationStatus(authData)
        .then((userInformation) => {
          if (userInformation.jwt) {
            config.$set({
              user: { ...userInformation, isAuthenticated: true },
            });

            // Setting local storage for guided setup
            localStorage.setItem(
              ALT_TOKEN,
              userInformation.jwt.alternativeToken,
            );
            localStorage.setItem(
              REFRESH_TOKEN,
              userInformation.jwt.refreshToken,
            );

            fetchFMSResponse(true);

            if (isGuidedSignIn) {
              sendGuidedSetupStatusThenExit('success');
              return;
            }

            focusCache.home.$clear();

            const { favouriteShowId } =
              (PageNavigation.getCurrentState() as {
                favouriteShowId: string;
              }) || {};

            if (favouriteShowId) {
              tenApi.pages
                .getMyShowData()
                .then((myShowsData) => {
                  config.$set({
                    myShowsIds: myShowsData?.items.map((myShow) => myShow.id),
                  });

                  openShowPage(favouriteShowId);
                })
                .catch(() => {
                  openShowPage(favouriteShowId);
                });

              return;
            }

            const playerData:
              | StandardCarouselItem
              | LiveCarouselItem = PageNavigation.getCurrentState();
            if ((playerData as LiveCarouselItem)?.liveShow) {
              PageNavigation.replaceRoute(
                `/player/live/${(playerData as LiveCarouselItem).key}`,
                playerData,
              );
            } else if (
              (playerData as StandardCarouselItem)?.playbackApiEndpoint
            ) {
              PageNavigation.replaceRoute(
                `/player/vod/${(playerData as StandardCarouselItem).id}`,
                playerData,
              );
            } else {
              PageNavigation.replaceRoute('/');
            }
          }
        })
        .catch((err) => {
          if (isGuidedSignIn) {
            sendGuidedSetupStatusThenExit('error');
          }
          catchApiError(err);
        });
    }
  }, 5000);

  useInterval(() => {
    const timeStamp = Math.round(new Date().getTime() / 1000);

    if ((authData?.expiry as number) <= timeStamp) {
      getAuthCode();
    }
  }, 60 * 1000);

  const { uapLiteActivationBackgroundImageUrl = ActivationScreenBg } =
    config.$get()?.app || {};

  return (
    <Nav.Horizontal id={pageId}>
      <div className={styles.screenContainer}>
        <img
          src={uapLiteActivationBackgroundImageUrl}
          className={styles.backgroundImage}
        />
        <div className={styles.contentContainer}>
          <div className={styles.contentHeader}>
            Sign in to enjoy the best of 10
          </div>
          <div className={styles.contentOne}>
            <div className={styles.content}>
              <span className={styles.number}>1</span>
              <p>{ACTIVATION_TEXT}</p>
            </div>
            <p className={styles.website}>10play.com.au/activate</p>
          </div>
          <div className={styles.contentTwo}>
            <div className={styles.content}>
              <span className={styles.number}>2</span>
              <p>When asked, all you need to do is enter this code:</p>
            </div>
            <p className={styles.entryCode}>{authData?.code}</p>
          </div>
          <div className={styles.buttonContainer}>
            <p>We'll log you in automatically right here.</p>
            {!isGuidedSignIn && (
              <CtvButton
                navId={continueButton}
                className={styles.button}
                onClick={handleBackNavigation}
              >
                Continue browsing for now
              </CtvButton>
            )}
            {!isGuidedSignIn && (
              <CtvButton
                navId={FAQButton}
                className={styles.button}
                onClick={onFaqClick}
              >
                Why do I need to sign in?
              </CtvButton>
            )}
          </div>
        </div>
      </div>
    </Nav.Horizontal>
  );
};

export default ActivationScreen;
